import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Minus, Plus } from 'lucide-react';
import { Button, Input, cn } from '@scio3/ui';

/** Scale font size to fit inside the 180px circle */
function getFontSize(value: number): string {
  const digits = String(value).length;
  if (digits <= 2) return '3.75rem'; // 60px
  if (digits === 3) return '2.75rem'; // 44px
  if (digits === 4) return '2rem'; // 32px
  return '1.5rem'; // 24px for 5+
}

type ScreenCountSelectorProps = {
  value: number;
  onChange: (count: number) => void;
  min: number;
  max?: number;
  /** Price info text to show below (e.g. "Your Plan: Pro Plus $8.33/screen/month") */
  planInfoText?: string;
  /**
   * Called when user tries to decrease below min (all screens in use).
   * If provided, opens a screen management dialog instead of just disabling the button.
   * Matching scio1 behavior: decrease => open Screen Management modal to remove devices.
   */
  onDecreaseBlocked?: () => void;
  /** Hide the 180px teal counter circle; show only the +/- stepper.
   *  Used by tiered add-on dialogs that already have visual tier cards above. */
  hideCircle?: boolean;
  /** Lock all interaction (e.g. when "Enable for all screens" is on for OptiDev). */
  disabled?: boolean;
};

/**
 * Screen count selector matching scio1's Step 1 layout:
 * - Large teal circle with number
 * - +/- touch-spin below
 */
export function ScreenCountSelector({
  value,
  onChange,
  min,
  max = 9999,
  planInfoText,
  onDecreaseBlocked,
  hideCircle = false,
  disabled = false,
}: ScreenCountSelectorProps) {
  const { t } = useTranslation();

  const handleDecrement = useCallback(() => {
    if (disabled) return;
    // Below zero never makes sense — silently no-op instead of popping
    // Screen Management (which would say "remove 0 screens to reduce more").
    if (value <= 0) return;
    if (value <= min) {
      // At minimum (still > 0) — can't decrease further, open screen management
      onDecreaseBlocked?.();
      return;
    }
    onChange(value - 1);
  }, [value, min, onChange, onDecreaseBlocked, disabled]);

  const handleIncrement = useCallback(() => {
    if (disabled) return;
    if (value < max) onChange(value + 1);
  }, [value, max, onChange, disabled]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      const raw = e.target.value.replace(/\D/g, '');
      if (!raw) {
        onChange(min);
        return;
      }
      const num = parseInt(raw, 10);
      onChange(Math.max(min, Math.min(max, num)));
    },
    [min, max, onChange, disabled],
  );

  return (
    <div className="flex flex-col items-center gap-6">
      {/* Large teal circle with screen count — matching scio1 .screenCountLabel */}
      {!hideCircle && (
        <div className="flex items-center justify-center rounded-full bg-primary w-[180px] h-[180px]">
          <span className="font-light text-white" style={{ fontSize: getFontSize(value) }}>
            {value}
          </span>
        </div>
      )}

      {/* +/- touch-spin — matching scio1 touch-spin component */}
      <div className="flex items-center gap-0">
        <Button
          data-testid="screen-count-decrement-btn"
          variant="default"
          size="icon-sm"
          onClick={handleDecrement}
          disabled={disabled || value <= 0 || (value <= min && !onDecreaseBlocked)}
          aria-label={t('Decrease screen count')}
          className={cn(
            'rounded-r-none shrink-0',
            disabled || value <= 0 || (value <= min && !onDecreaseBlocked)
              ? 'opacity-50 cursor-not-allowed'
              : 'cursor-pointer',
          )}
        >
          <Minus className="h-4 w-4" />
        </Button>
        <Input
          data-testid="screen-count-value"
          type="text"
          inputMode="numeric"
          value={value}
          onChange={handleInputChange}
          disabled={disabled}
          className="w-16 text-center text-base font-medium rounded-none border-x-0 h-8"
          aria-label={t('Number of screens')}
        />
        <Button
          data-testid="screen-count-increment-btn"
          variant="default"
          size="icon-sm"
          onClick={handleIncrement}
          disabled={disabled || value >= max}
          aria-label={t('Increase screen count')}
          className={cn(
            'rounded-l-none shrink-0',
            disabled || value >= max ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
          )}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {/* Plan info text below — matching scio1 .text-warning-step */}
      {planInfoText && <p className="text-sm text-muted-foreground text-center">{planInfoText}</p>}
    </div>
  );
}
